import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Plus, X, Handshake, Buildings, User, CurrencyDollar, MagnifyingGlass, FunnelSimple, Trash, FileXls, FilePdf, Warning, Bell, ClockCounterClockwise, GearSix, PencilSimple, ArrowUp, ArrowDown, ListBullets, CalendarDots, Tag, CheckSquare, ChatCircle, Paperclip, UsersThree, Clock, Link as LinkIcon, UploadSimple, Check, Archive } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { CardDetailPanel } from "./CardDetailPanel";

// Searchable contact/company picker
function ContactSearchPicker({ contacts, value, onChange, placeholder = "Buscar empresa..." }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const selected = contacts.find(c => c.id === value);

  const filtered = query.trim()
    ? contacts.filter(c =>
        (c.company_name || "").toLowerCase().includes(query.toLowerCase()) ||
        (c.contact_name || "").toLowerCase().includes(query.toLowerCase())
      ).slice(0, 10)
    : contacts.slice(0, 10);

  return (
    <div className="relative" data-testid="contact-search-picker">
      {selected ? (
        <div className="flex items-center gap-2 h-8 px-2 border rounded-lg bg-white text-xs">
          <Buildings size={12} className="text-blue-500 shrink-0" />
          <span className="flex-1 truncate font-semibold text-slate-800">{selected.company_name || selected.contact_name || "Sin nombre"}</span>
          <button onClick={() => { onChange(""); setQuery(""); }} className="text-slate-400 hover:text-red-500 shrink-0"><X size={12} /></button>
        </div>
      ) : (
        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="w-full h-8 pl-7 pr-2 border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            data-testid="contact-search-input"
          />
        </div>
      )}
      {open && !selected && (
        <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-xs text-slate-400 p-2 text-center">Sin resultados</p>
          ) : filtered.map(c => (
            <button key={c.id} onClick={() => { onChange(c.id); setQuery(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 text-xs flex items-center gap-2 border-b last:border-0 transition-colors">
              <Buildings size={11} className="text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 truncate">{c.company_name || "Sin empresa"}</p>
                {c.contact_name && <p className="text-[10px] text-slate-500 truncate">{c.contact_name}</p>}
              </div>
            </button>
          ))}
          <button onClick={() => { onChange(""); setOpen(false); }}
            className="w-full text-left px-3 py-1.5 text-[10px] text-slate-400 hover:text-blue-600 hover:bg-slate-50 border-t">
            Sin vincular empresa
          </button>
        </div>
      )}
      {open && !selected && <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />}
    </div>
  );
}

export function NegociacionesBoard({ onNavigateToContact }) {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [negociaciones, setNegociaciones] = useState([]);
  const [stages, setStages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(null); // stage_id or null
  const [newForm, setNewForm] = useState({ title: "", amount: "", contact_id: "", notes: "" });
  const [dragItem, setDragItem] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("kanban"); // kanban, lista, calendario
  const [sellerFilter, setSellerFilter] = useState("");
  const [staleAlerts, setStaleAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [selectedNeg, setSelectedNeg] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [newStage, setNewStage] = useState({ name: "", color: "#64748B" });
  const [editingStage, setEditingStage] = useState(null);
  const [allLabels, setAllLabels] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [negRes, stagesRes, contactsRes, alertsRes, labelsRes, usersRes] = await Promise.all([
        axios.get(`${API}/negociaciones?include_archived=${showArchived}`, { headers }),
        axios.get(`${API}/negociaciones/stages`, { headers }),
        axios.get(`${API}/comercial`, { headers }),
        axios.get(`${API}/negociaciones/alerts/stale?days=7`, { headers }),
        axios.get(`${API}/negociaciones/labels/all`, { headers }),
        axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
      ]);
      setNegociaciones(negRes.data);
      setStages(stagesRes.data);
      setContacts(contactsRes.data);
      setStaleAlerts(alertsRes.data?.alerts || []);
      setAllLabels(labelsRes.data || []);
      setAllUsers(usersRes.data || []);
    } catch { toast.error("Error al cargar negociaciones"); }
    setLoading(false);
  }, [token, showArchived]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (stageId) => {
    const contact = contacts.find(c => c.id === newForm.contact_id);
    try {
      const res = await axios.post(`${API}/negociaciones`, {
        title: newForm.title,
        amount: parseFloat(newForm.amount) || 0,
        contact_id: newForm.contact_id || null,
        company_name: contact?.company_name || "",
        contact_name: contact?.contact_name || "",
        stage: stageId,
        notes: newForm.notes,
      }, { headers });
      setNegociaciones(prev => [...prev, res.data]);
      setShowNewForm(null);
      setNewForm({ title: "", amount: "", contact_id: "", notes: "" });
      toast.success("Negociacion creada");
    } catch { toast.error("Error al crear"); }
  };

  const handleDragStart = (e, neg) => {
    setDragItem(neg);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStage(stageId);
  };

  const handleDrop = async (e, stageId) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!dragItem || dragItem.stage === stageId) { setDragItem(null); return; }
    // Optimistic update
    setNegociaciones(prev => prev.map(n => n.id === dragItem.id ? { ...n, stage: stageId } : n));
    try {
      await axios.patch(`${API}/negociaciones/${dragItem.id}/stage`, { stage: stageId }, { headers });
    } catch {
      setNegociaciones(prev => prev.map(n => n.id === dragItem.id ? { ...n, stage: dragItem.stage } : n));
      toast.error("Error al mover");
    }
    setDragItem(null);
  };

  const handleDragEnd = () => { setDragItem(null); setDragOverStage(null); };

  const handleDelete = async (neg) => {
    if (!window.confirm(`¿Eliminar ${neg.number}?`)) return;
    try {
      await axios.delete(`${API}/negociaciones/${neg.id}`, { headers });
      setNegociaciones(prev => prev.filter(n => n.id !== neg.id));
      toast.success("Negociacion eliminada");
    } catch { toast.error("Error al eliminar"); }
  };

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`${API}/negociaciones/export/excel`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `negociaciones_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch { toast.error("Error al exportar Excel"); }
  };

  const handleExportPDF = async () => {
    try {
      const res = await axios.get(`${API}/negociaciones/export/pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `reporte_negociaciones_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch { toast.error("Error al exportar PDF"); }
  };

  const handleSendAlerts = async () => {
    setSendingAlerts(true);
    try {
      const res = await axios.post(`${API}/negociaciones/alerts/notify?days=7`, {}, { headers });
      toast.success(res.data.message || "Alertas enviadas");
    } catch { toast.error("Error al enviar alertas"); }
    setSendingAlerts(false);
  };

  const handleAddStage = async () => {
    if (!newStage.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      await axios.post(`${API}/negociaciones/stages`, newStage, { headers });
      setNewStage({ name: "", color: "#64748B" });
      const res = await axios.get(`${API}/negociaciones/stages`, { headers });
      setStages(res.data);
      toast.success("Etapa creada");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleUpdateStage = async (stageId, data) => {
    try {
      await axios.put(`${API}/negociaciones/stages/${stageId}`, data, { headers });
      const res = await axios.get(`${API}/negociaciones/stages`, { headers });
      setStages(res.data);
      setEditingStage(null);
      toast.success("Etapa actualizada");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDeleteStage = async (stageId) => {
    if (!window.confirm("¿Eliminar esta etapa?")) return;
    try {
      await axios.delete(`${API}/negociaciones/stages/${stageId}`, { headers });
      const res = await axios.get(`${API}/negociaciones/stages`, { headers });
      setStages(res.data);
      toast.success("Etapa eliminada");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleReorderStage = async (stageId, direction) => {
    const idx = stages.findIndex(s => s.id === stageId);
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === stages.length - 1)) return;
    const newStages = [...stages];
    [newStages[idx], newStages[idx + direction]] = [newStages[idx + direction], newStages[idx]];
    setStages(newStages);
    try {
      await axios.put(`${API}/negociaciones/stages/reorder`, { order: newStages.map(s => s.id) }, { headers });
    } catch { toast.error("Error al reordenar"); fetchData(); }
  };


  const openCard = (neg) => {
    setSelectedNeg(neg);
  };


  // Filter by search + seller
  const filtered = negociaciones.filter(n => {
    const matchSearch = !search.trim() || [n.company_name, n.contact_name, n.number, n.title].some(f => (f || "").toLowerCase().includes(search.toLowerCase()));
    const matchSeller = !sellerFilter || n.created_by_id === sellerFilter;
    return matchSearch && matchSeller;
  });

  const sellers = [...new Map(negociaciones.map(n => [n.created_by_id, n.created_by_name])).entries()].filter(([id]) => id);

  const getStageItems = (stageId) => filtered.filter(n => n.stage === stageId);
  const getStageTotal = (stageId) => getStageItems(stageId).reduce((sum, n) => sum + (n.amount || 0), 0);
  const staleIds = new Set(staleAlerts.map(a => a.id));

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="h-full flex flex-col" data-testid="negociaciones-board">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Handshake size={24} weight="duotone" className="text-blue-600" /> Proyectos
            <span className="text-base font-normal text-slate-400 ml-1">({negociaciones.length})</span>
          </h1>
          {/* View tabs */}
          <div className="flex gap-1 mt-2" data-testid="neg-view-tabs">
            {[{id:"kanban",label:"Kanban"},{id:"lista",label:"Lista"},{id:"calendario",label:"Calendario"}].map(v => (
              <button key={v.id} onClick={() => setViewMode(v.id)}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${viewMode === v.id ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                data-testid={`neg-view-${v.id}`}>{v.label}</button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." className="h-8 text-xs pl-8 w-44" data-testid="neg-search" />
          </div>
          <select value={sellerFilter} onChange={e => setSellerFilter(e.target.value)} className="h-8 text-xs border rounded-lg px-2 bg-white" data-testid="neg-seller-filter">
            <option value="">Todos los vendedores</option>
            {sellers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <Button variant="outline" size="sm" className="h-8 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={handleExportExcel} data-testid="neg-export-excel">
            <FileXls size={14} className="mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs text-red-700 border-red-200 hover:bg-red-50" onClick={handleExportPDF} data-testid="neg-export-pdf">
            <FilePdf size={14} className="mr-1" /> PDF
          </Button>
          {user?.role === "admin" && (
            <Button variant="outline" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700" onClick={() => setShowConfig(true)} data-testid="neg-config-btn">
              <GearSix size={16} />
            </Button>
          )}
          <Button variant="outline" size="sm" className={`h-8 text-xs ${showArchived ? "bg-amber-50 text-amber-700 border-amber-200" : "text-slate-500"}`}
            onClick={() => setShowArchived(!showArchived)} data-testid="neg-toggle-archived">
            <Archive size={14} className="mr-1" /> {showArchived ? "Archivadas" : "Archivo"}
          </Button>
        </div>
      </div>

      {/* Stage Configuration Modal */}
      {showConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="neg-config-modal">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowConfig(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10 rounded-t-2xl">
              <div className="flex items-center gap-2">
                <GearSix size={18} className="text-slate-600" />
                <h3 className="font-bold text-slate-900">Configurar Etapas</h3>
              </div>
              <button onClick={() => setShowConfig(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Existing stages */}
              {stages.map(s => (
                <div key={s.id} className="flex items-center gap-3 p-3 border rounded-xl group hover:border-blue-200 transition-colors" data-testid={`stage-config-${s.id}`}>
                  {editingStage === s.id ? (
                    <>
                      <input type="color" value={s.color} onChange={e => handleUpdateStage(s.id, { color: e.target.value })} className="w-8 h-8 rounded-lg border cursor-pointer shrink-0" />
                      <input value={s.name} onChange={e => {
                        setStages(prev => prev.map(st => st.id === s.id ? {...st, name: e.target.value} : st));
                      }} className="flex-1 h-8 px-2 border rounded-lg text-sm font-semibold" />
                      <Button size="sm" className="h-7 text-xs" onClick={() => { const st = stages.find(x => x.id === s.id); handleUpdateStage(s.id, { name: st.name }); }}>OK</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditingStage(null); fetchData(); }}>
                        <X size={12} />
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="w-5 h-5 rounded-full shrink-0 border" style={{ backgroundColor: s.color }} />
                      <span className="flex-1 text-sm font-semibold text-slate-800">{s.name}</span>
                      <span className="text-[10px] text-slate-400">{getStageItems(s.id).length} neg.</span>
                      <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-all">
                        <button onClick={() => handleReorderStage(s.id, -1)} className="text-slate-400 hover:text-blue-600" data-testid={`stage-up-${s.id}`}><ArrowUp size={10} weight="bold" /></button>
                        <button onClick={() => handleReorderStage(s.id, 1)} className="text-slate-400 hover:text-blue-600" data-testid={`stage-down-${s.id}`}><ArrowDown size={10} weight="bold" /></button>
                      </div>
                      <button onClick={() => setEditingStage(s.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 transition-all"><PencilSimple size={14} /></button>
                      <button onClick={() => handleDeleteStage(s.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all"><Trash size={14} /></button>
                    </>
                  )}
                </div>
              ))}

              {/* Add new stage */}
              <div className="border-t pt-4">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Agregar nueva etapa</p>
                <div className="flex items-center gap-2">
                  <input type="color" value={newStage.color} onChange={e => setNewStage({...newStage, color: e.target.value})} className="w-8 h-8 rounded-lg border cursor-pointer shrink-0" data-testid="new-stage-color" />
                  <Input value={newStage.name} onChange={e => setNewStage({...newStage, name: e.target.value})} placeholder="Nombre de etapa..." className="h-8 text-sm flex-1" data-testid="new-stage-name" />
                  <Button size="sm" className="h-8 text-xs" onClick={handleAddStage} data-testid="new-stage-submit">
                    <Plus size={12} className="mr-1" /> Agregar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stale Alerts Banner */}
      {staleAlerts.length > 0 && (
        <div className="mb-4 border border-amber-200 bg-amber-50 rounded-xl overflow-hidden" data-testid="stale-alerts-banner">
          <button onClick={() => setShowAlerts(!showAlerts)} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-amber-100/50 transition-colors">
            <div className="flex items-center gap-2">
              <Warning size={16} className="text-amber-600" />
              <span className="text-xs font-bold text-amber-800">{staleAlerts.length} negociacion(es) sin movimiento por mas de 7 dias</span>
            </div>
            <span className="text-[10px] text-amber-600 font-semibold">{showAlerts ? "Ocultar" : "Ver detalles"}</span>
          </button>
          {showAlerts && (
            <div className="px-4 pb-3 space-y-1.5 border-t border-amber-200">
              {staleAlerts.slice(0, 8).map(a => (
                <div key={a.id} className="flex items-center justify-between py-1.5 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-700">{a.number}</span>
                    <span className="text-slate-500">{a.company_name || a.title}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{a.stage_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-amber-700">{a.days_stale}d</span>
                    <span className="text-slate-500">${(a.amount || 0).toLocaleString("es-MX")}</span>
                  </div>
                </div>
              ))}
              {(user?.role === "admin" || user?.role === "subgerente") && (
                <Button size="sm" variant="outline" className="h-7 text-[10px] mt-2 border-amber-300 text-amber-700 hover:bg-amber-100" onClick={handleSendAlerts} disabled={sendingAlerts} data-testid="send-stale-alerts">
                  <Bell size={12} className="mr-1" /> {sendingAlerts ? "Enviando..." : "Enviar alertas al equipo"}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Kanban Board */}
      {viewMode === "kanban" && (
      <div className="flex-1 overflow-x-auto">
        <div className="flex gap-3 min-w-max pb-4" style={{ minHeight: "calc(100vh - 220px)" }}>
          {stages.map(stage => {
            const items = getStageItems(stage.id);
            const total = getStageTotal(stage.id);
            const isDragOver = dragOverStage === stage.id;
            return (
              <div
                key={stage.id}
                className={`flex flex-col w-72 shrink-0 rounded-xl transition-all ${isDragOver ? "ring-2 ring-blue-400 bg-blue-50/50" : "bg-slate-50/80"}`}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={() => setDragOverStage(null)}
                onDrop={(e) => handleDrop(e, stage.id)}
                data-testid={`stage-column-${stage.id}`}
              >
                {/* Stage Header */}
                <div className="px-3 py-2.5 border-b shrink-0" style={{ borderBottomColor: stage.color + "60" }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                      <span className="text-xs font-bold text-slate-800">{stage.name}</span>
                      <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full font-bold">{items.length}</span>
                    </div>
                  </div>
                  <p className="text-lg font-extrabold mt-1" style={{ color: stage.color }}>
                    ${total.toLocaleString("es-MX")}
                  </p>
                </div>

                {/* Add button */}
                <div className="px-2 py-1.5 shrink-0">
                  <button
                    onClick={() => setShowNewForm(showNewForm === stage.id ? null : stage.id)}
                    className="w-full text-center text-[10px] text-slate-400 hover:text-blue-600 py-1.5 border border-dashed border-slate-300 rounded-lg hover:border-blue-400 transition-colors"
                    data-testid={`add-neg-${stage.id}`}
                  >
                    <Plus size={12} className="inline mr-0.5" /> Negociacion rapida
                  </button>
                </div>

                {/* New form */}
                {showNewForm === stage.id && (
                  <div className="px-2 pb-2 shrink-0">
                    <div className="bg-white border-2 border-blue-300 rounded-xl p-3 space-y-2 shadow-lg">
                      <Input value={newForm.title} onChange={e => setNewForm({...newForm, title: e.target.value})} placeholder="Titulo" className="h-7 text-xs" data-testid="neg-form-title" />
                      <Input type="number" value={newForm.amount} onChange={e => setNewForm({...newForm, amount: e.target.value})} placeholder="Monto ($)" className="h-7 text-xs" data-testid="neg-form-amount" />
                      <ContactSearchPicker contacts={contacts} value={newForm.contact_id} onChange={v => setNewForm({...newForm, contact_id: v})} placeholder="Buscar empresa..." />
                      <Textarea value={newForm.notes} onChange={e => setNewForm({...newForm, notes: e.target.value})} placeholder="Notas..." className="text-xs min-h-[40px]" />
                      <div className="flex gap-1.5">
                        <Button size="sm" className="h-7 text-xs flex-1" onClick={() => handleCreate(stage.id)} data-testid="neg-form-submit">Crear</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowNewForm(null)}><X size={12} /></Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Cards */}
                <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-2">
                  {items.map(neg => (
                    <div
                      key={neg.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, neg)}
                      onDragEnd={handleDragEnd}
                      onClick={() => openCard(neg)}
                      className={`border rounded-xl p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-all group ${dragItem?.id === neg.id ? "opacity-40" : ""} ${staleIds.has(neg.id) ? "border-l-2 border-l-amber-400" : "border-l-[3px]"}`}
                      style={{ borderLeftColor: staleIds.has(neg.id) ? undefined : stage.color, backgroundColor: stage.color + "08" }}
                      data-testid={`neg-card-${neg.id}`}
                    >
                      {/* Labels */}
                      {(neg.label_ids || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-1.5">
                          {(neg.label_ids || []).map(lid => {
                            const lb = allLabels.find(l => l.id === lid);
                            return lb ? <span key={lid} className="text-[8px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: lb.color }}>{lb.name}</span> : null;
                          })}
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-bold text-slate-500">{neg.number}</span>
                        <div className="flex items-center gap-1">
                          {neg.payment_status && (
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${neg.payment_status === "pagado" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                              {neg.payment_status === "pagado" ? "PAGADO" : "NO PAGADO"}
                            </span>
                          )}
                          {user?.role === "admin" && (
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(neg); }} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all" data-testid={`delete-neg-${neg.id}`}>
                              <Trash size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                      {neg.title && neg.title !== `Negociacion ${neg.number}` && (
                        <p className="text-xs font-semibold text-slate-700 mb-1 line-clamp-2">{neg.title}</p>
                      )}
                      <p className="text-base font-extrabold text-slate-900 mb-2">${(neg.amount || 0).toLocaleString("es-MX")}</p>
                      {neg.contact_name && (
                        <div className="flex items-center gap-1.5 mb-1">
                          <User size={11} className="text-blue-500 shrink-0" />
                          <span className="text-[10px] text-blue-600 font-semibold truncate cursor-pointer hover:underline" onClick={() => neg.contact_id && onNavigateToContact?.(neg.contact_id)}>{neg.contact_name}</span>
                        </div>
                      )}
                      {neg.company_name && (
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Buildings size={11} className="text-slate-400 shrink-0" />
                          <span className="text-[10px] text-slate-500 truncate">{neg.company_name}</span>
                        </div>
                      )}
                      {/* Due date indicator */}
                      {neg.due_date && (
                        <div className={`flex items-center gap-1 text-[10px] font-semibold mb-1 ${new Date(neg.due_date) < new Date() ? "text-red-600" : "text-slate-500"}`}>
                          <Clock size={10} /> {format(new Date(neg.due_date + "T12:00:00"), "dd MMM", { locale: es })}
                          {new Date(neg.due_date) < new Date() && <span className="text-[8px] bg-red-100 text-red-600 px-1 rounded">VENCIDA</span>}
                        </div>
                      )}
                      {/* Checklist progress */}
                      {(neg.checklists || []).length > 0 && (() => {
                        const total = neg.checklists.reduce((s, cl) => s + (cl.items?.length || 0), 0);
                        const done = neg.checklists.reduce((s, cl) => s + (cl.items?.filter(i => i.checked).length || 0), 0);
                        return total > 0 ? (
                          <div className="flex items-center gap-1.5 mb-1">
                            <CheckSquare size={10} className={done === total ? "text-green-600" : "text-slate-400"} />
                            <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden"><div className={`h-full rounded-full ${done === total ? "bg-green-500" : "bg-blue-500"}`} style={{ width: `${(done / total) * 100}%` }} /></div>
                            <span className="text-[9px] text-slate-500 font-mono">{done}/{total}</span>
                          </div>
                        ) : null;
                      })()}
                      <div className="flex items-center justify-between text-[10px] text-slate-400">
                        <span>{neg.created_at ? format(new Date(neg.created_at), "dd MMM yyyy", { locale: es }) : ""}</span>
                        {staleIds.has(neg.id) ? (
                          <span className="text-[9px] font-bold text-amber-600 flex items-center gap-0.5"><Warning size={10} /> Estancada</span>
                        ) : (
                          <span className="text-[10px] text-slate-400">{neg.created_by_name}</span>
                        )}
                      </div>
                      {/* Members avatars */}
                      {(neg.members || []).length > 0 && (
                        <div className="flex items-center gap-0.5 mt-1.5">
                          {neg.members.slice(0, 4).map(m => (
                            <div key={m.id} className="w-5 h-5 rounded-full bg-blue-600 text-white text-[8px] font-bold flex items-center justify-center" title={m.name}>
                              {(m.name || "?").slice(0, 2).toUpperCase()}
                            </div>
                          ))}
                          {neg.members.length > 4 && <span className="text-[9px] text-slate-400">+{neg.members.length - 4}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* List View */}
      {viewMode === "lista" && (
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="neg-list-view">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b text-left">
                <th className="px-3 py-2.5 font-semibold text-slate-600 text-xs">#</th>
                <th className="px-3 py-2.5 font-semibold text-slate-600 text-xs">Titulo</th>
                <th className="px-3 py-2.5 font-semibold text-slate-600 text-xs">Monto</th>
                <th className="px-3 py-2.5 font-semibold text-slate-600 text-xs">Etapa</th>
                <th className="px-3 py-2.5 font-semibold text-slate-600 text-xs">Empresa</th>
                <th className="px-3 py-2.5 font-semibold text-slate-600 text-xs">Contacto</th>
                <th className="px-3 py-2.5 font-semibold text-slate-600 text-xs">Prox. Contacto</th>
                <th className="px-3 py-2.5 font-semibold text-slate-600 text-xs">Pago</th>
                <th className="px-3 py-2.5 font-semibold text-slate-600 text-xs">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(neg => {
                const stage = stages.find(s => s.id === neg.stage);
                return (
                  <tr key={neg.id} className="border-b hover:bg-blue-50/50 cursor-pointer transition-colors" onClick={() => openCard(neg)} data-testid={`neg-row-${neg.id}`}>
                    <td className="px-3 py-2 text-xs font-bold text-slate-500">{neg.number}</td>
                    <td className="px-3 py-2 text-xs font-semibold text-slate-800">{neg.title || "—"}</td>
                    <td className="px-3 py-2 text-xs font-extrabold text-slate-900">${(neg.amount || 0).toLocaleString("es-MX")}</td>
                    <td className="px-3 py-2"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{backgroundColor: (stage?.color || "#64748b") + "18", color: stage?.color}}>{stage?.name || neg.stage}</span></td>
                    <td className="px-3 py-2 text-xs text-slate-600">{neg.company_name || "—"}</td>
                    <td className="px-3 py-2 text-xs text-blue-600 font-medium">{neg.contact_name || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{neg.next_contact_date ? format(new Date(neg.next_contact_date), "dd MMM yyyy", { locale: es }) : "—"}</td>
                    <td className="px-3 py-2">{neg.payment_status ? <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${neg.payment_status === "pagado" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{neg.payment_status === "pagado" ? "PAGADO" : "NO PAGADO"}</span> : "—"}</td>
                    <td className="px-3 py-2 text-[10px] text-slate-400">{neg.created_at ? format(new Date(neg.created_at), "dd/MM/yy", { locale: es }) : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center text-sm text-slate-400 py-10">Sin negociaciones</p>}
        </div>
      )}

      {/* Calendar View */}
      {viewMode === "calendario" && <NegCalendarView negociaciones={filtered} stages={stages} onClickNeg={openCard} />}

      {/* Card Detail / Edit Panel */}
      {selectedNeg && (
        <CardDetailPanel
          neg={selectedNeg}
          stages={stages}
          contacts={contacts}
          allLabels={allLabels}
          allUsers={allUsers}
          onClose={() => setSelectedNeg(null)}
          onUpdated={(updated) => {
            setNegociaciones(prev => prev.map(n => n.id === updated.id ? updated : n));
            setSelectedNeg(updated);
          }}
          onNavigateToContact={onNavigateToContact}
          onLabelsChanged={() => axios.get(`${API}/negociaciones/labels/all`, { headers }).then(r => setAllLabels(r.data || [])).catch(() => {})}
          onArchived={(updated) => {
            if (updated.archived) {
              setNegociaciones(prev => prev.filter(n => n.id !== updated.id));
              setSelectedNeg(null);
            } else {
              setNegociaciones(prev => prev.map(n => n.id === updated.id ? updated : n));
              setSelectedNeg(updated);
            }
          }}
          onCopied={(newNeg) => {
            setNegociaciones(prev => [newNeg, ...prev]);
          }}
        />
      )}
    </div>
  );
}


// Calendar mini-view for negociaciones by next_contact_date
function NegCalendarView({ negociaciones, stages, onClickNeg }) {
  const [month, setMonth] = useState(new Date());
  const stageMap = Object.fromEntries(stages.map(s => [s.id, s]));

  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(year, m, 1).getDay();
  const daysInMonth = new Date(year, m + 1, 0).getDate();

  // Group negs by next_contact_date
  const byDate = {};
  negociaciones.forEach(n => {
    const d = n.next_contact_date || n.created_at?.slice(0, 10);
    if (d) {
      if (!byDate[d]) byDate[d] = [];
      byDate[d].push(n);
    }
  });

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

  return (
    <div className="bg-white border rounded-xl p-4" data-testid="neg-calendar-view">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setMonth(new Date(year, m - 1, 1))} className="text-slate-400 hover:text-blue-600 text-sm font-bold px-2">←</button>
        <h3 className="text-sm font-bold text-slate-800">{monthNames[m]} {year}</h3>
        <button onClick={() => setMonth(new Date(year, m + 1, 1))} className="text-slate-400 hover:text-blue-600 text-sm font-bold px-2">→</button>
      </div>
      <div className="grid grid-cols-7 gap-px">
        {["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"].map(d => (
          <div key={d} className="text-center text-[10px] font-bold text-slate-400 py-1">{d}</div>
        ))}
        {days.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const dateStr = `${year}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const dayNegs = byDate[dateStr] || [];
          const isToday = dateStr === new Date().toISOString().slice(0, 10);
          return (
            <div key={d} className={`min-h-[64px] border rounded-lg p-1 ${isToday ? "bg-blue-50 border-blue-300" : "border-slate-100"}`}>
              <p className={`text-[10px] font-bold ${isToday ? "text-blue-600" : "text-slate-500"}`}>{d}</p>
              {dayNegs.slice(0, 3).map(n => {
                const st = stageMap[n.stage] || {};
                return (
                  <div key={n.id} onClick={() => onClickNeg(n)} className="text-[8px] px-1 py-0.5 rounded mt-0.5 cursor-pointer truncate font-semibold" style={{ backgroundColor: (st.color || "#64748b") + "20", color: st.color || "#64748b" }}>
                    {n.number} ${(n.amount || 0).toLocaleString("es-MX")}
                  </div>
                );
              })}
              {dayNegs.length > 3 && <p className="text-[8px] text-slate-400 text-center">+{dayNegs.length - 3} más</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
